import {
  addDays,
  addMinutes,
  format,
  formatDistance,
  isToday,
  isTomorrow,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

import { CalendarDay, TimeZone } from "~/shared.types";

export const convertTo12HourFormat = (time24: string): string => {
  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
};

export const TIME_ZONE_LIST = [
  {
    code: "ET",
    name: "Eastern Time",
    city: "New York",
    iana: "America/New_York",
  },
  {
    code: "CT",
    name: "Central Time (Chicago)",
    city: "Chicago",
    iana: "America/Chicago",
  },
  {
    code: "MT",
    name: "Mountain Time (Denver)",
    city: "Denver",
    iana: "America/Denver",
  },
  {
    code: "MST",
    name: "Mountain Standard Time",
    city: "Phoenix",
    iana: "America/Phoenix",
  },
  {
    code: "PT",
    name: "Pacific Time",
    city: "Los Angeles",
    iana: "America/Los_Angeles",
  },
  {
    code: "AKT",
    name: "Alaska Time",
    city: "Anchorage",
    iana: "America/Anchorage",
  },
  {
    code: "HAT",
    name: "Hawaii–Aleutian Time",
    city: "Honolulu",
    iana: "Pacific/Honolulu",
  },
] as const;

export const DEFAULT_TIMEZONE: TimeZone = TIME_ZONE_LIST[0];

type TimeZoneCode = (typeof TIME_ZONE_LIST)[number]["code"];

export const TIMEZONES: Record<TimeZoneCode, TimeZone> = Object.fromEntries(
  TIME_ZONE_LIST.map((tz) => [tz.code, tz]),
) as Record<TimeZoneCode, TimeZone>;

export const TIMEZONE_TO_IANA: Record<string, string> = Object.fromEntries(
  TIME_ZONE_LIST.map((tz) => [tz.code, tz.iana]),
);

export const IANA_TO_CODE: Record<string, string> = Object.fromEntries(
  TIME_ZONE_LIST.map((tz) => [tz.iana, tz.code]),
);

export const timeZoneCodeOrDefault = (iana: string | undefined): string =>
  (iana ? IANA_TO_CODE[iana] : undefined) ?? TIMEZONES.ET.code;

export const convertTimeToTimeZone = (
  time: string,
  fromTimeZone: string,
  toTimeZone: string,
  referenceDate: Date,
): string => {
  const timeMatch = time.match(/(\d+):(\d+)/);
  if (!timeMatch) return time;

  const hour = parseInt(timeMatch[1], 10);
  const minute = parseInt(timeMatch[2], 10) || 0;

  const fromIana = TIMEZONE_TO_IANA[fromTimeZone] ?? TIMEZONE_TO_IANA.ET;
  const toIana = TIMEZONE_TO_IANA[toTimeZone] ?? TIMEZONE_TO_IANA.ET;
  if (fromIana === toIana) return time;

  const dateInFromZone = new Date(toZonedTime(referenceDate, fromIana));
  dateInFromZone.setHours(hour, minute, 0, 0);
  const inToZone = toZonedTime(fromZonedTime(dateInFromZone, fromIana), toIana);

  return format(inToZone, "HH:mm");
};

export const getFirstBusinessDayOfMonth = (
  year: number,
  month: number,
): Date => {
  const firstDayOfMonth = new Date(year, month, 1);
  const dayOfWeek = firstDayOfMonth.getDay();

  if (dayOfWeek === 0) {
    return addDays(firstDayOfMonth, 1);
  } else if (dayOfWeek === 6) {
    return addDays(firstDayOfMonth, 2);
  } else {
    return firstDayOfMonth;
  }
};

export const getNextBusinessDay = (date: Date): Date => {
  const nextDay = addDays(date, 1);
  const dayOfWeek = nextDay.getDay();

  if (dayOfWeek === 0) {
    return addDays(nextDay, 1);
  } else if (dayOfWeek === 6) {
    return addDays(nextDay, 2);
  } else {
    return nextDay;
  }
};

export const formatDateForAPI = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatAppointmentTime = (
  startTime: string,
  duration: number,
  timeZone: TimeZone,
): string => {
  const [hours, minutes] = startTime.split(":").map(Number);
  const startDate = addMinutes(
    new Date(2000, 0, 1, 0, 0, 0),
    hours * 60 + minutes,
  );
  const endDate = addMinutes(startDate, duration);
  const endTime24 = format(endDate, "HH:mm");

  return `${convertTo12HourFormat(startTime)}-${convertTo12HourFormat(endTime24)} (${timeZone.code})`;
};

export const calendarDayFromDate = (date: Date): CalendarDay => {
  return {
    date,
    dayOfWeek: format(date, "EEE"),
    dayNumber: date.getDate(),
    isToday: isToday(date),
    isTomorrow: isTomorrow(date),
    isAvailable: true,
  };
};

export const formatDayLabel = (day: CalendarDay): string => {
  if (day.isToday) return "Today";
  if (day.isTomorrow) return "Tomorrow";
  return day.date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

export const getSelectedDayMeta = (
  day: CalendarDay,
): { label: string; distanceInDays: string | null } => ({
  label: formatDayLabel(day),
  distanceInDays: isToday(day.date)
    ? null
    : formatDistance(day.date, new Date(), { addSuffix: true }),
});

export const getAdjacentMonth = (
  currentMonthYear: { month: string; year: number },
  offset: number,
): Date => {
  const base = new Date(
    `${currentMonthYear.month} 1, ${currentMonthYear.year}`,
  );
  let monthIndex = base.getMonth() + offset;
  let year = base.getFullYear();
  if (monthIndex > 11) {
    monthIndex = 0;
    year += 1;
  }
  if (monthIndex < 0) {
    monthIndex = 11;
    year -= 1;
  }
  return new Date(year, monthIndex, 1);
};

export const formatMonthYear = (date: Date): string =>
  date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

const CALENDAR_DAY_ARIA_LABEL_FORMAT = "EEEE, MMMM d, yyyy";

export const getCalendarDayAriaLabel = (
  date: Date,
  {
    isSelected = false,
    isUnavailable = false,
  }: { isSelected?: boolean; isUnavailable?: boolean } = {},
): string => {
  const parts = [format(date, CALENDAR_DAY_ARIA_LABEL_FORMAT)];

  if (isToday(date)) parts.push("today");

  if (isSelected) parts.push("selected");

  if (isUnavailable) parts.push("unavailable");

  return parts.join(", ");
};
